/**
 * Kubernetes settings management page component for admins.
 */
import React, { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { useSettings } from '../contexts/SettingsContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { updateSettings, SettingsUpdate } from '../api/settings';
import './KubernetesSettingsPage.css';

export const KubernetesSettingsPage: React.FC = () => {
  const { settings, refreshSettings } = useSettings();
  const { isConnected, on, off } = useWebSocket();
  const [kubernetesClusterDetectionEnabled, setKubernetesClusterDetectionEnabled] = useState(true);
  
  // Default patterns (matching backend defaults)
  const defaultControlplanePatterns = '(.+)-controlplane-.*';
  const defaultWorkerPatterns = `{cluster}-worker-\\d+$
{cluster}-worker-.*-\\d+$`;
  
  const [controlplanePatterns, setControlplanePatterns] = useState(defaultControlplanePatterns);
  const [workerPatterns, setWorkerPatterns] = useState(defaultWorkerPatterns);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Test section state
  const defaultTestHostnames = `software-controlplane-01
software-worker-01
software-dev-controlplane-01
software-dev-worker-cpu-01
software-dev-worker-cpu-mem-01
my-cluster-controlplane-master
my-cluster-worker-05`;
  const [testHostnames, setTestHostnames] = useState(defaultTestHostnames);
  const [testResults, setTestResults] = useState<Array<{
    hostname: string;
    isControlplane: boolean;
    controlplaneClusterName: string | null;
    matchedControlplanePattern: string | null;
    isWorker: boolean;
    workerClusterName: string | null;
    matchedWorkerPattern: string | null;
    errors: string[];
  }>>([]);
  const [controlplaneValidationErrors, setControlplaneValidationErrors] = useState<string[]>([]);
  const [testSectionExpanded, setTestSectionExpanded] = useState(false);

  // Parse patterns from string (space or newline separated)
  const parsePatterns = React.useCallback((patternString: string): string[] => {
    if (!patternString || !patternString.trim()) {
      return [];
    }
    const patterns: string[] = [];
    for (const line of patternString.split('\n')) {
      patterns.push(...line.split(/\s+/).filter(p => p.trim()));
    }
    return patterns.filter(p => p.trim());
  }, []);

  // Validate control plane patterns - must have exactly 1 capture group
  const validateControlplanePatterns = React.useCallback((patterns: string[]): string[] => {
    const errors: string[] = [];
    for (const pattern of patterns) {
      try {
        // Count capture groups by finding unescaped opening parentheses that are not lookahead/lookbehind
        // This is a simplified check - we look for ( that is not preceded by \ and not followed by ?
        let captureGroups = 0;
        for (let i = 0; i < pattern.length; i++) {
          if (pattern[i] === '(' && (i === 0 || pattern[i - 1] !== '\\')) {
            // Check if it's a non-capturing group (?:, (?=, (?!, (?<=, (?<!
            if (i + 1 < pattern.length && pattern[i + 1] === '?') {
              // Non-capturing group, skip
              continue;
            }
            captureGroups++;
          }
        }
        
        if (captureGroups === 0) {
          errors.push(`Pattern "${pattern}" has no capture groups. Control plane patterns must have exactly 1 capture group for cluster name extraction.`);
        } else if (captureGroups > 1) {
          errors.push(`Pattern "${pattern}" has ${captureGroups} capture groups. Control plane patterns must have exactly 1 capture group for cluster name extraction.`);
        }
      } catch (e: any) {
        errors.push(`Pattern "${pattern}" is invalid regex: ${e.message}`);
      }
    }
    return errors;
  }, []);

  useEffect(() => {
    if (settings) {
      setKubernetesClusterDetectionEnabled(settings.kubernetes_cluster_detection_enabled ?? true);
      setControlplanePatterns(settings.kubernetes_controlplane_patterns || defaultControlplanePatterns);
      setWorkerPatterns(settings.kubernetes_worker_patterns || defaultWorkerPatterns);
    }
  }, [settings]);

  // Validate control plane patterns when they change
  useEffect(() => {
    const patterns = parsePatterns(controlplanePatterns);
    const errors = validateControlplanePatterns(patterns);
    setControlplaneValidationErrors(errors);
  }, [controlplanePatterns, parsePatterns, validateControlplanePatterns]);

  // Register WebSocket event handlers for settings updates
  useEffect(() => {
    const handleSettingsUpdate = async (message: { event: string; data?: any }) => {
      const { settings_data } = message.data || {};
      
      if (settings_data) {
        // Update local state from WebSocket message
        setKubernetesClusterDetectionEnabled(settings_data.kubernetes_cluster_detection_enabled ?? true);
        setControlplanePatterns(settings_data.kubernetes_controlplane_patterns || defaultControlplanePatterns);
        setWorkerPatterns(settings_data.kubernetes_worker_patterns || defaultWorkerPatterns);
        // Refresh settings context
        await refreshSettings();
      }
    };

    const unsubscribe = on('settings_update', handleSettingsUpdate);

    return () => {
      unsubscribe();
    };
  }, [on, off, refreshSettings]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const updates: SettingsUpdate = {
        kubernetes_cluster_detection_enabled: kubernetesClusterDetectionEnabled,
        kubernetes_controlplane_patterns: controlplanePatterns.trim() || null,
        kubernetes_worker_patterns: workerPatterns.trim() || null,
      };

      await updateSettings(updates);
      setSuccess('Kubernetes settings updated successfully!');
      await refreshSettings();
    } catch (err: any) {
      let errorMessage = 'Failed to update Kubernetes settings. Please try again.';
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setKubernetesClusterDetectionEnabled(settings.kubernetes_cluster_detection_enabled ?? true);
      setControlplanePatterns(settings.kubernetes_controlplane_patterns || defaultControlplanePatterns);
      setWorkerPatterns(settings.kubernetes_worker_patterns || defaultWorkerPatterns);
      setError(null);
      setSuccess(null);
    }
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset Kubernetes settings to defaults? This cannot be undone.')) {
      setKubernetesClusterDetectionEnabled(true);
      setControlplanePatterns(defaultControlplanePatterns);
      setWorkerPatterns(defaultWorkerPatterns);
      setError(null);
      setSuccess(null);
    }
  };


  // Test patterns against hostnames
  const testPatterns = () => {
    const hostnameList = testHostnames.split('\n').map(h => h.trim()).filter(h => h);
    const controlplanePatternsList = parsePatterns(controlplanePatterns);
    const workerPatternsList = parsePatterns(workerPatterns);
    
    // Validate control plane patterns
    const validationErrors = validateControlplanePatterns(controlplanePatternsList);
    setControlplaneValidationErrors(validationErrors);
    
    // Extract cluster names from control plane hostnames first
    const clusterNames = new Set<string>();
    for (const hostname of hostnameList) {
      const hostnameLower = hostname.toLowerCase();
      for (const pattern of controlplanePatternsList) {
        try {
          const regex = new RegExp(pattern, 'i');
          const match = hostnameLower.match(regex);
          if (match && match[1]) {
            clusterNames.add(match[1].trim());
          }
        } catch (e) {
          // Skip invalid patterns
        }
      }
    }
    
    // Test each hostname
    const results = hostnameList.map(hostname => {
      const result: {
        hostname: string;
        isControlplane: boolean;
        controlplaneClusterName: string | null;
        matchedControlplanePattern: string | null;
        isWorker: boolean;
        workerClusterName: string | null;
        matchedWorkerPattern: string | null;
        errors: string[];
      } = {
        hostname,
        isControlplane: false,
        controlplaneClusterName: null,
        matchedControlplanePattern: null,
        isWorker: false,
        workerClusterName: null,
        matchedWorkerPattern: null,
        errors: [],
      };
      
      const hostnameLower = hostname.toLowerCase();
      
      // Test control plane patterns
      for (const pattern of controlplanePatternsList) {
        try {
          const regex = new RegExp(pattern, 'i');
          const match = hostnameLower.match(regex);
          if (match) {
            result.isControlplane = true;
            result.matchedControlplanePattern = pattern;
            if (match[1]) {
              result.controlplaneClusterName = match[1].trim();
              clusterNames.add(result.controlplaneClusterName);
            }
            break;
          }
        } catch (e: any) {
          result.errors.push(`Control plane pattern error: ${e.message}`);
        }
      }
      
      // Test worker patterns (only if not a control plane)
      if (!result.isControlplane) {
        for (const pattern of workerPatternsList) {
          try {
            let matched = false;
            let matchedPattern = pattern;
            
            // If pattern contains {cluster}, try with each known cluster name
            if (pattern.includes('{cluster}')) {
              for (const clusterName of clusterNames) {
                const clusterNameEscaped = clusterName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const patternWithCluster = pattern.replace('{cluster}', clusterNameEscaped);
                const regex = new RegExp(patternWithCluster, 'i');
                if (regex.test(hostnameLower)) {
                  matched = true;
                  matchedPattern = pattern;
                  result.workerClusterName = clusterName;
                  break;
                }
              }
            } else {
              // Regular pattern - extract cluster name from capture groups
              const regex = new RegExp(pattern, 'i');
              const match = hostnameLower.match(regex);
              if (match) {
                matched = true;
                matchedPattern = pattern;
                // Join all capture groups with hyphens
                if (match.length > 1) {
                  result.workerClusterName = match.slice(1).filter(g => g).join('-').trim();
                }
              }
            }
            
            if (matched) {
              result.isWorker = true;
              result.matchedWorkerPattern = matchedPattern;
              break;
            }
          } catch (e: any) {
            result.errors.push(`Worker pattern error: ${e.message}`);
          }
        }
      }
      
      return result;
    });
    
    setTestResults(results);
  };

  if (!settings) {
    return (
      <AdminLayout>
        <div className="kubernetes-settings-page">
          <div className="kubernetes-settings-container">
            <div className="loading-message">Loading Kubernetes settings...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="kubernetes-settings-page">
        <div className="kubernetes-settings-container">
          <div className="admin-breadcrumb">
            <Link to="/admin">Admin</Link>
            <span className="admin-breadcrumb-separator">/</span>
            <span className="admin-breadcrumb-current">Kubernetes</span>
          </div>
          <h1>Kubernetes Settings</h1>

          <form onSubmit={handleSubmit} className="kubernetes-settings-form" role="form">
            {error && (
              <div className="error-message" role="alert">
                <strong>Error:</strong> {error}
              </div>
            )}

            {success && (
              <div className="success-message" role="alert">
                {success}
              </div>
            )}

            <section className="settings-section">
              <div className="form-group">
                <label className="checkbox-label" htmlFor="kubernetes-cluster-detection">
                  <input
                    id="kubernetes-cluster-detection"
                    type="checkbox"
                    checked={kubernetesClusterDetectionEnabled}
                    onChange={(e) => {
                      setKubernetesClusterDetectionEnabled(e.target.checked);
                      setError(null);
                    }}
                    disabled={isLoading}
                  />
                  Enable Kubernetes cluster detection
                </label>
                <small className="form-hint">
                  Automatically detect and group Kubernetes clusters from VM names. Clusters are identified by control plane nodes and their associated worker nodes using configurable regex patterns.
                </small>
              </div>

              <div className="patterns-row">
                <div className="form-group">
                  <label htmlFor="controlplane-patterns">Control Plane Patterns</label>
                  <textarea
                    id="controlplane-patterns"
                    value={controlplanePatterns}
                    onChange={(e) => {
                      setControlplanePatterns(e.target.value);
                      setError(null);
                    }}
                    disabled={isLoading}
                    placeholder="Enter regex patterns (one per line or space-separated)"
                    rows={5}
                    className="pattern-input"
                  />
                  <small className="form-hint">
                    Regex patterns for detecting control plane nodes. Multiple patterns can be specified (space or newline separated). The first capture group will be used as the cluster name. Example: <code>(.+)-controlplane-.*</code>
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="worker-patterns">Worker Patterns</label>
                  <textarea
                    id="worker-patterns"
                    value={workerPatterns}
                    onChange={(e) => {
                      setWorkerPatterns(e.target.value);
                      setError(null);
                    }}
                    disabled={isLoading}
                    placeholder="Enter regex patterns (one per line or space-separated)"
                    rows={5}
                    className="pattern-input"
                  />
                  <small className="form-hint">
                    Regex patterns for detecting worker nodes. Multiple patterns can be specified (space or newline separated). 
                    You can use <code>{'{cluster}'}</code> placeholder to reference the cluster name extracted from control plane patterns. 
                    Examples: <code>{'{cluster}'}-worker-\d+$</code> (matches "software-worker-01" when cluster is "software") or <code>(.+)-worker-\d+$</code> (extracts cluster name from capture group).
                  </small>
                </div>
              </div>
            </section>

            <section className="settings-section">
              <button
                type="button"
                className="collapsible-header"
                onClick={() => setTestSectionExpanded(!testSectionExpanded)}
                aria-expanded={testSectionExpanded}
                aria-label={testSectionExpanded ? 'Collapse test section' : 'Expand test section'}
              >
                <h2>Test Patterns</h2>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={testSectionExpanded ? 'expanded' : ''}
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {testSectionExpanded && (
                <>
              <div className="form-group">
                <label htmlFor="test-hostnames">Test Hostnames</label>
                <textarea
                  id="test-hostnames"
                  value={testHostnames}
                  onChange={(e) => {
                    setTestHostnames(e.target.value);
                    setTestResults([]);
                  }}
                  disabled={isLoading}
                  placeholder="Enter hostnames to test (one per line)"
                  rows={8}
                  className="pattern-input"
                />
                <small className="form-hint">
                  Enter hostnames (one per line) to test how they match against the patterns above. Click "Test Patterns" to see results.
                </small>
                <button
                  type="button"
                  className="btn-test"
                  onClick={testPatterns}
                  disabled={isLoading}
                >
                  Test Patterns
                </button>
              </div>

              {controlplaneValidationErrors.length > 0 && (
                <div className="validation-errors">
                  <strong>Control Plane Pattern Validation Errors:</strong>
                  <ul>
                    {controlplaneValidationErrors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {testResults.length > 0 && (
                <div className="test-results">
                  <h3>Test Results</h3>
                  <div className="test-results-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Hostname</th>
                          <th>Type</th>
                          <th>Cluster Name</th>
                          <th>Matched Pattern</th>
                          <th>Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testResults.map((result, idx) => (
                          <tr key={idx}>
                            <td className="hostname-cell">
                              <code>{result.hostname}</code>
                            </td>
                            <td>
                              {result.isControlplane && (
                                <span className="badge badge-controlplane">Control Plane</span>
                              )}
                              {result.isWorker && (
                                <span className="badge badge-worker">Worker</span>
                              )}
                              {!result.isControlplane && !result.isWorker && (
                                <span className="badge badge-none">No Match</span>
                              )}
                            </td>
                            <td>
                              {result.controlplaneClusterName && (
                                <code>{result.controlplaneClusterName}</code>
                              )}
                              {result.workerClusterName && (
                                <code>{result.workerClusterName}</code>
                              )}
                              {!result.controlplaneClusterName && !result.workerClusterName && (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              {result.matchedControlplanePattern && (
                                <code className="pattern-code">{result.matchedControlplanePattern}</code>
                              )}
                              {result.matchedWorkerPattern && (
                                <code className="pattern-code">{result.matchedWorkerPattern}</code>
                              )}
                              {!result.matchedControlplanePattern && !result.matchedWorkerPattern && (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              {result.errors.length > 0 ? (
                                <ul className="error-list">
                                  {result.errors.map((error, errIdx) => (
                                    <li key={errIdx} className="error-item">{error}</li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
                </>
              )}
            </section>

            <div className="form-actions">
              <button
                type="button"
                className="btn-reset"
                onClick={handleReset}
                disabled={isLoading}
              >
                Reset Changes
              </button>
              <button
                type="button"
                className="btn-reset-defaults"
                onClick={handleResetToDefaults}
                disabled={isLoading}
              >
                Reset to Defaults
              </button>
              <button
                type="submit"
                className="btn-save"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};
